import React, { useState, useEffect, useRef } from 'react';
import { 
  ArrowRight, 
  Rocket, 
  Shield, 
  Zap, 
  TrendingUp, 
  Users, 
  DollarSign, 
  Clock, 
  Layers, 
  CheckCircle, 
  ArrowUpRight, 
  Star, 
  BarChart3,
  PieChart,
  LineChart,
  Wallet,
  Target,
  Cpu,
  Activity,
  Globe
} from 'lucide-react';

interface HomePageProps {
  onLaunchPlatform: () => void;
  onNavigateToPage: (page: string) => void;
}

const HomePage: React.FC<HomePageProps> = ({ onLaunchPlatform, onNavigateToPage }) => {
  // EKKO Logo URL
  const EKKO_LOGO = "https://raw.githubusercontent.com/ekkoplat/logos/refs/heads/main/EkkoLogo3%20(2).png";
  
  // Stats for the hero section
  const stats = [
    {
      value: '$2.4M',
      label: 'TVL',
      icon: <DollarSign className="h-6 w-6" />,
      change: '+24% this month',
    },
    {
      value: '2.5K',
      label: 'TRADERS',
      icon: <Users className="h-6 w-6" />,
      change: 'active users',
    },
    {
      value: '99.9%',
      label: 'UPTIME',
      icon: <Shield className="h-6 w-6" />,
      change: 'platform reliability',
    },
    {
      value: '<100ms',
      label: 'LATENCY',
      icon: <Zap className="h-6 w-6" />,
      change: 'trade execution',
    }
  ];
  
  // Features data
  const features = [
    {
      title: 'SNIPER BOT',
      description: 'Lightning-fast automated token sniping with advanced filters and MEV protection for optimal entry points.',
      icon: <Target className="h-6 w-6" />,
      color: 'green',
      backgroundImage: 'https://raw.githubusercontent.com/TokentruthAI/TokenTruthLogos/refs/heads/main/19888.png'
    },
    {
      title: 'COPY TRADER',
      description: 'Follow successful traders and automatically replicate their strategies with customizable risk management.',
      icon: <Users className="h-6 w-6" />,
      color: 'gray',
      backgroundImage: 'https://raw.githubusercontent.com/TokentruthAI/TokenTruthLogos/refs/heads/main/1891.png'
    },
    {
      title: 'WHALE TRACKER',
      description: 'Monitor large transactions and follow whale movements in real-time across the Solana ecosystem.',
      icon: <Activity className="h-6 w-6" />,
      color: 'green',
      backgroundImage: 'https://raw.githubusercontent.com/TokentruthAI/TokenTruthLogos/refs/heads/main/9999.png'
    },
    {
      title: 'SMART WATCHLIST',
      description: 'Advanced token and wallet tracking with custom filters, alerts, and real-time portfolio monitoring.',
      icon: <Globe className="h-6 w-6" />,
      color: 'gray',
      backgroundImage: 'https://raw.githubusercontent.com/TokentruthAI/TokenTruthLogos/refs/heads/main/216.png'
    },
    {
      title: 'AUTO TRADER',
      description: 'Intelligent automated trading with advanced strategies, risk management, and portfolio optimization.',
      icon: <Cpu className="h-6 w-6" />,
      color: 'green',
      backgroundImage: 'https://raw.githubusercontent.com/TokentruthAI/TokenTruthLogos/refs/heads/main/889.png'
    },
    {
      title: 'CANVAS INTERFACE',
      description: 'Revolutionary widget-based trading interface that adapts to your trading style and preferences.',
      icon: <Layers className="h-6 w-6" />,
      color: 'gray',
      backgroundImage: 'https://raw.githubusercontent.com/TokentruthAI/TokenTruthLogos/refs/heads/main/212.png'
    }
  ];
  
  // How it works steps
  const steps = [
    {
      title: 'CONNECT',
      description: 'Connect your Solana wallet and access the EKKO platform with enterprise-grade security.',
      icon: <Wallet className="h-6 w-6" />,
      details: [
        'Phantom & Solflare wallet support',
        'Non-custodial security',
        'Multi-wallet management',
        'Hardware wallet integration'
      ]
    },
    {
      title: 'CONFIGURE',
      description: 'Set up your trading strategy with our advanced automation tools and risk management.',
      icon: <Cpu className="h-6 w-6" />,
      details: [
        'Custom trading bots',
        'Risk management rules',
        'Copy trading setup',
        'Portfolio allocation'
      ]
    },
    {
      title: 'TRADE',
      description: 'Execute trades with lightning speed and precision using our professional trading tools.',
      icon: <Zap className="h-6 w-6" />,
      details: [
        'Sub-100ms execution',
        'MEV protection',
        'Advanced order types',
        'Real-time analytics'
      ]
    }
  ];

  return (
    <div className="relative overflow-hidden bg-white min-h-screen">
      {/* Background architectural elements */}
      <div className="bg-orbs"></div>
      <div className="vertical-lines"></div>
      
      {/* Large Background Text */}
      <div className="fixed inset-0 pointer-events-none select-none overflow-hidden z-0 flex flex-col items-center justify-center">
        <div className="text-[12rem] font-black text-gray-300/40 leading-none text-center">
          ULTIMATE
        </div>
        <div className="text-[12rem] font-black text-gray-300/40 leading-none text-center">
          TOKEN
        </div>
        <div className="text-[12rem] font-black text-gray-300/40 leading-none text-center">
          TOOLKIT
        </div>
      </div>
      
      {/* Hero Section */}
      <section className="section corner-crosses pt-16 md:pt-24 lg:pt-32 pb-0 relative">
        
        <div className="container mx-auto max-w-7xl px-4 h-full">
          <div className="grid lg:grid-cols-2 gap-12 items-stretch relative z-10 min-h-[70vh]">
            
            {/* Left Side - Large Warrior Image */}
            <div className="relative order-2 lg:order-1 flex items-end" style={{ marginBottom: '30px' }}>
              <div className="relative max-w-lg mx-auto lg:max-w-none w-full">
                {/* Warrior Image - Raw, no container */}
                <div className="relative">
                  <img 
                    src="https://raw.githubusercontent.com/TokentruthAI/TokenTruthLogos/refs/heads/main/ChatGPT%20Image%20Jun%207%2C%202025%2C%2008_43_24%20AM.png"
                    alt="EKKO Warrior" 
                    className="w-full h-auto drop-shadow-2xl scale-110 block" 
                    style={{ marginBottom: '0', lineHeight: '0' }}
                  />
                </div>
              </div>
            </div>
            
            {/* Right Side - Content */}
            <div className="text-center lg:text-left order-1 lg:order-2">
              
              {/* EKKO Logo - Above Title */}
              <div className="flex justify-center lg:justify-start mb-6 mt-12">
                <img 
                  src={EKKO_LOGO}
                  alt="EKKO Logo" 
                  className="h-36 w-auto drop-shadow-lg"
                />
              </div>
              
              {/* Main Title */}
              <h1 className="text-5xl md:text-6xl lg:text-7xl font-black mb-8 leading-tight">
                <span className="text-gradient block -mb-2">
                  ULTIMATE
                </span>
                <span className="text-gray-900 text-3xl md:text-4xl lg:text-5xl">
                  TOKEN TOOLKIT
                </span>
              </h1>
              
              {/* Subtitle */}
              <p className="text-xl md:text-2xl text-gray-600 mb-12 leading-relaxed font-light max-w-2xl mx-auto lg:mx-0">
                Professional-grade trading tools, advanced automation, and real-time market intelligence 
                for the next generation of DeFi traders on Solana.
              </p>
              
              {/* CTA Buttons */}
              <div className="flex flex-col sm:flex-row gap-4 justify-center lg:justify-start">
                <button 
                  onClick={onLaunchPlatform}
                  className="btn-primary btn-lg group relative overflow-hidden"
                >
                  <span className="relative z-10 flex items-center justify-center">
                    <Rocket className="h-5 w-5 mr-2 group-hover:scale-110 transition-transform" />
                    LAUNCH PLATFORM
                  </span>
                </button>
                
                <button 
                  onClick={() => onNavigateToPage('features')}
                  className="btn-outline btn-lg group"
                >
                  <span className="mr-2 group-hover:mr-3 transition-all">EXPLORE FEATURES</span>
                  <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
                </button>
              </div>
            </div>
          </div>
        </div>
      </section>
      
      {/* Features Section */}
      <section className="section corner-crosses pt-0 pb-16 relative">
        <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-green-400/60 to-transparent">
          <div className="absolute left-1/2 top-1/2 transform -translate-x-1/2 -translate-y-1/2 w-6 h-6 bg-green-500 rounded-full border-2 border-white shadow-lg"></div>
        </div>
        
        <div className="container mx-auto max-w-6xl px-4">
          
          {/* Section Header */}
          <div className="flex items-center mb-12 pt-24">
            <div className="cross-decoration w-4 h-4 mr-4"></div>
            <div>
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-2">PLATFORM FEATURES</h2>
              <p className="text-gray-600">Professional trading tools built for Solana DeFi</p>
            </div>
          </div>
          
          {/* Features Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {features.map((feature, index) => (
              <div 
                key={index} 
                className="arch-card glassmorphism p-8 h-full group hover:scale-105 transition-all duration-300 relative overflow-hidden"
              >
                {/* Background warrior image */}
                <div className="absolute inset-0 z-0">
                  <img 
                    src={feature.backgroundImage}
                    alt=""
                    className={`w-full h-full object-cover opacity-15 transition-opacity duration-300 group-hover:opacity-20 ${
                      feature.title === 'WHALE TRACKER' ? 'object-[center_20%]' : ''
                    }`}
                  />
                </div>
                
                {/* Overlay to ensure content readability */}
                <div className="absolute inset-0 bg-white/10 z-5"></div>
                
                {/* Content layer */}
                <div className="relative z-10 flex flex-col h-full">
                  <div className="cross-decoration absolute top-0 left-0 w-3 h-3"></div>
                  
                  <div className={`h-14 w-14 rounded-xl bg-${feature.color === 'green' ? 'green' : 'gray'}-100/80 backdrop-blur-xl border border-${feature.color === 'green' ? 'green' : 'gray'}-200/50 flex items-center justify-center mb-6 group-hover:scale-110 transition-transform shadow-lg`}>
                    <div className={`text-${feature.color === 'green' ? 'green' : 'gray'}-600`}>
                      {feature.icon}
                    </div>
                  </div>
                  
                  <h3 className="text-xl font-semibold mb-4 text-gray-900 group-hover:text-green-600 transition-colors">
                    {feature.title}
                  </h3>
                  <p className="text-gray-700 leading-relaxed font-medium mb-auto">{feature.description}</p>
                  
                  <div className="mt-auto pt-6">
                    <button 
                      onClick={onLaunchPlatform}
                      className="btn-primary btn-lg w-full group relative overflow-hidden"
                    >
                      <span className="relative z-10 flex items-center justify-center">
                        TRY NOW
                        <Rocket className="h-4 w-4 ml-2 group-hover:translate-x-1 transition-transform" />
                      </span>
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>
      
      {/* Roadmap & Whitepaper Summary Section */}
      <section className="section corner-crosses py-16 relative">
        <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-green-400/60 to-transparent">
          <div className="absolute left-1/2 top-1/2 transform -translate-x-1/2 -translate-y-1/2 w-6 h-6 bg-green-500 rounded-full border-2 border-white shadow-lg"></div>
        </div>
        
        <div className="container mx-auto max-w-6xl px-4">
          
          {/* Section Header */}
          <div className="text-center mb-16 pt-24">
            <div className="inline-flex items-center justify-center p-2 glassmorphism backdrop-blur-xl border border-green-200/50 mb-8">
              <BarChart3 className="h-5 w-5 text-green-500 mr-2" />
              <span className="text-sm font-medium text-gray-700 uppercase tracking-wide">LEARN MORE ABOUT EKKO</span>
            </div>
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">EXPLORE OUR VISION</h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Dive deeper into EKKO's strategic roadmap and comprehensive technical documentation.
            </p>
          </div>
          
          {/* Roadmap & Whitepaper Grid */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 items-stretch">
            
            {/* Roadmap Card */}
            <div className="arch-card glassmorphism p-8 h-full group hover:scale-105 transition-all duration-300 relative overflow-hidden">
              <div className="cross-decoration absolute top-6 left-6 w-4 h-4"></div>
              <div className="cross-decoration absolute top-6 right-6 w-4 h-4"></div>
              
              <div className="relative z-10 flex flex-col h-full">
                <div className="h-16 w-16 rounded-xl bg-green-100/80 backdrop-blur-xl border border-green-200/50 flex items-center justify-center mb-6 group-hover:scale-110 transition-transform shadow-lg">
                  <TrendingUp className="h-8 w-8 text-green-600" />
                </div>
                
                <h3 className="text-2xl font-bold mb-4 text-gray-900 group-hover:text-green-600 transition-colors">
                  ROADMAP
                </h3>
                
                <p className="text-gray-700 leading-relaxed mb-6 flex-grow">
                  Discover EKKO's strategic development plan, upcoming features, and milestone achievements. 
                  Our roadmap outlines the future of DeFi trading tools and platform evolution.
                </p>
                
                {/* Roadmap highlights */}
                <div className="space-y-3 mb-8">
                  <div className="flex items-center">
                    <CheckCircle className="h-5 w-5 text-green-500 mr-3 flex-shrink-0" />
                    <span className="text-gray-600 text-sm">Q1 2025: Advanced AI Trading Bots</span>
                  </div>
                  <div className="flex items-center">
                    <CheckCircle className="h-5 w-5 text-green-500 mr-3 flex-shrink-0" />
                    <span className="text-gray-600 text-sm">Q2 2025: Cross-Chain Integration</span>
                  </div>
                  <div className="flex items-center">
                    <CheckCircle className="h-5 w-5 text-green-500 mr-3 flex-shrink-0" />
                    <span className="text-gray-600 text-sm">Q3 2025: Mobile Trading App</span>
                  </div>
                </div>
                
                <div className="flex justify-center">
                  <button 
                    onClick={() => onNavigateToPage('roadmap')}
                    className="btn-primary btn-lg group relative overflow-hidden"
                  >
                    <span className="relative z-10 flex items-center justify-center">
                      <TrendingUp className="h-5 w-5 mr-2 group-hover:scale-110 transition-transform" />
                      VIEW ROADMAP
                    </span>
                  </button>
                </div>
              </div>
            </div>
            
            {/* Faded Divider - Only visible on larger screens */}
            <div className="hidden lg:block absolute left-1/2 top-1/2 transform -translate-x-1/2 -translate-y-1/2 h-32 w-px bg-gradient-to-b from-transparent via-gray-300/50 to-transparent"></div>
            
            {/* Whitepaper Card */}
            <div className="arch-card glassmorphism p-8 h-full group hover:scale-105 transition-all duration-300 relative overflow-hidden">
              <div className="cross-decoration absolute top-6 left-6 w-4 h-4"></div>
              <div className="cross-decoration absolute top-6 right-6 w-4 h-4"></div>
              
              <div className="relative z-10 flex flex-col h-full">
                <div className="h-16 w-16 rounded-xl bg-gray-100/80 backdrop-blur-xl border border-gray-200/50 flex items-center justify-center mb-6 group-hover:scale-110 transition-transform shadow-lg">
                  <PieChart className="h-8 w-8 text-gray-600" />
                </div>
                
                <h3 className="text-2xl font-bold mb-4 text-gray-900 group-hover:text-green-600 transition-colors">
                  WHITEPAPER
                </h3>
                
                <p className="text-gray-700 leading-relaxed mb-6 flex-grow">
                  Explore the technical foundation and architecture behind EKKO's revolutionary trading platform. 
                  Comprehensive documentation of our algorithms, security measures, and innovative solutions.
                </p>
                
                {/* Whitepaper highlights */}
                <div className="space-y-3 mb-8">
                  <div className="flex items-center">
                    <CheckCircle className="h-5 w-5 text-green-500 mr-3 flex-shrink-0" />
                    <span className="text-gray-600 text-sm">Technical Architecture Overview</span>
                  </div>
                  <div className="flex items-center">
                    <CheckCircle className="h-5 w-5 text-green-500 mr-3 flex-shrink-0" />
                    <span className="text-gray-600 text-sm">Security & MEV Protection</span>
                  </div>
                  <div className="flex items-center">
                    <CheckCircle className="h-5 w-5 text-green-500 mr-3 flex-shrink-0" />
                    <span className="text-gray-600 text-sm">Tokenomics & Economics</span>
                  </div>
                </div>
                
                <div className="flex justify-center">
                  <button 
                    onClick={() => onNavigateToPage('whitepaper')}
                    className="btn-outline btn-lg group"
                  >
                    <span className="mr-2 group-hover:mr-3 transition-all">READ WHITEPAPER</span>
                    <PieChart className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
      
      {/* How It Works Section */}
      <section className="section corner-crosses py-16 relative">
        <div className="horizontal-cross"></div>
        
        <div className="container mx-auto max-w-6xl px-4">
          
          {/* Section Header */}
          <div className="text-center mb-16">
            <div className="inline-flex items-center justify-center p-2 glassmorphism backdrop-blur-xl border border-green-200/50 mb-8">
              <Rocket className="h-5 w-5 text-green-500 mr-2" />
              <span className="text-sm font-medium text-gray-700 uppercase tracking-wide">SIMPLE THREE-STEP PROCESS</span>
            </div>
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">HOW EKKO WORKS</h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Get started with professional DeFi trading in minutes with our intuitive platform.
            </p>
          </div>
          
          {/* Steps Grid */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-16">
            {steps.map((step, index) => (
              <div key={index} className="relative">
                {/* Step number */}
                <div className="absolute -top-6 left-1/2 transform -translate-x-1/2 w-12 h-12 rounded-full bg-green-500 border-4 border-white flex items-center justify-center text-lg font-bold text-white z-10">
                  {index + 1}
                </div>
                
                <div className="arch-card glassmorphism p-8 pt-12 h-full">
                  <div className="cross-decoration absolute top-6 right-6 w-3 h-3"></div>
                  
                  <div className="text-center mb-6">
                    <div className="h-16 w-16 rounded-full bg-green-100/50 mx-auto flex items-center justify-center mb-4 border border-green-200/50">
                      <div className="text-green-600">
                        {step.icon}
                      </div>
                    </div>
                    <h3 className="text-2xl font-bold text-gray-900 mb-3">{step.title}</h3>
                    <p className="text-gray-600">{step.description}</p>
                  </div>
                  
                  <ul className="space-y-3">
                    {step.details.map((detail, i) => (
                      <li key={i} className="flex items-start">
                        <CheckCircle className="h-5 w-5 text-green-500 mr-3 flex-shrink-0 mt-0.5" />
                        <span className="text-gray-600 text-sm">{detail}</span>
                      </li>
                    ))}
                  </ul>
                </div>
                
                {/* Connector line between steps */}
                {index < steps.length - 1 && (
                  <div className="hidden md:block absolute top-1/2 -right-4 w-8 connecting-line-horizontal">
                    <div className="connector-node right-0 top-1/2"></div>
                  </div>
                )}
              </div>
            ))}
          </div>
          
          <div className="text-center">
            <button 
              onClick={onLaunchPlatform}
              className="btn btn-primary btn-lg"
            >
              <Rocket className="h-5 w-5 mr-2" />
              START TRADING NOW
            </button>
          </div>
        </div>
      </section>
      
      {/* CTA Section */}
      <section className="section corner-crosses py-16 relative">
        <div className="horizontal-cross"></div>
        
        <div className="container mx-auto max-w-4xl px-4">
          <div className="glassmorphism p-12 text-center relative overflow-hidden">
            <div className="cross-decoration absolute top-6 left-6 w-4 h-4"></div>
            <div className="cross-decoration absolute top-6 right-6 w-4 h-4"></div>
            <div className="cross-decoration absolute bottom-6 left-6 w-4 h-4"></div>
            <div className="cross-decoration absolute bottom-6 right-6 w-4 h-4"></div>
            
            <div className="relative z-10">
              <h2 className="text-4xl md:text-5xl font-black mb-6 text-gradient">
                Ready to Trade Smarter?
              </h2>
              <p className="text-xl text-gray-600 mb-8 max-w-2xl mx-auto leading-relaxed">
                Join thousands of traders who've discovered the power of EKKO's revolutionary trading platform.
              </p>
              
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <button 
                  onClick={onLaunchPlatform}
                  className="btn btn-primary btn-lg group relative overflow-hidden"
                >
                  <span className="relative z-10 flex items-center">
                    <Rocket className="h-5 w-5 mr-2" />
                    LAUNCH PLATFORM
                  </span>
                </button>
                
                <button 
                  onClick={() => onNavigateToPage('whitepaper')}
                  className="btn btn-outline btn-lg group"
                >
                  <span className="mr-2 group-hover:mr-3 transition-all">READ WHITEPAPER</span>
                  <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
                </button>
              </div>
            </div>
          </div>
        </div>
      </section>
      
      {/* Platform Status */}
      <section className="py-8 border-t border-gray-200/50">
        <div className="container mx-auto max-w-6xl px-4">
          <div className="flex justify-center">
            <div className="inline-flex items-center glassmorphism px-6 py-3 border border-green-200/50">
              <div className="w-3 h-3 bg-green-500 rounded-full mr-3 animate-pulse"></div>
              <span className="text-green-600 font-medium text-sm uppercase tracking-wide">
                Platform Status: Live & Scaling
              </span>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default HomePage; 